import { isDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectOrderEntryByIdFromBlotter,
  selectTakeProfitErrors,
  selectTradeIdFromTakeProfitTile,
} from '@/store/orders/takeProfitOrder/selectors.ts';

type TakeProfitLimitPriceProps = {
  limitPrice: number | undefined;
  setLimitPrice: (value: number | undefined) => void;
  setCustomerPrice: (value: number | undefined) => void;
};

export const TakeProfitEditingLimitPrice = (props: TakeProfitLimitPriceProps) => {
  const { limitPrice, setLimitPrice, setCustomerPrice } = props;
  const tileId = useContext(TakeProfitContext).tileId;
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromTakeProfitTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const limitPriceError = errors?.limitPrice;

  const validateTakeProfit = useValidateTakeProfit();

  const handleLimitPriceChange = async (value: number | undefined) => {
    track('Order action', 'Limit price changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateTakeProfit({
        limitPrice: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setLimitPrice(value);

        if (isDefined(response?.data?.customerPrice)) {
          setCustomerPrice(response?.data?.customerPrice);
        }
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.limitPrice.label">
      <OmsFieldErrorWrapper errors={limitPriceError}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          maxDecimals={9}
          value={
            limitPrice ??
            (isDefined(orderFromBlotter?.limitPrice) ? (orderFromBlotter?.limitPrice as number) : undefined)
          }
          onChange={handleLimitPriceChange}
          data-e2e="limit-price"
          size="sm"
          state={isDefined(limitPriceError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
